import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IJobStatus,
  IPropertiesPanel,
  ISwitchOpenStatus,
} from 'interfaces/orchestrationDiagram/propertiesPanel.interface';
import {
  combineStatus,
  updateSteps,
} from 'utils/commonFunctions/CommonFunctions';
import { RootState } from '../store';
import { ORCHESTRATION_STATUSES } from 'utils/common-constants';
import {
  IOrchestrationServer,
  IStepInformation,
} from 'interfaces/orchestration.interface';

export const SLICE_KEY = 'propertiesPanel';

const initialState: IPropertiesPanel = {
  orchestrationInstancePanelStep: undefined,
  propertiesPanelSteps: [],
  currentJob: undefined,
};

const propertiesPanelSlice = createSlice({
  name: SLICE_KEY,
  initialState,
  reducers: {
    setInitialPropertiesPanelValues: (
      state,
      { payload }: PayloadAction<IPropertiesPanel>
    ) => {
      if (payload.orchestrationInstancePanelStep) {
        state.orchestrationInstancePanelStep = {
          ...payload.orchestrationInstancePanelStep,
          isOpen: state.orchestrationInstancePanelStep?.isOpen ?? payload.orchestrationInstancePanelStep.isOpen ?? true,
        } as typeof state.orchestrationInstancePanelStep;
      }
      state.propertiesPanelSteps = payload.propertiesPanelSteps.map((step) => {
        const existingStep = state.propertiesPanelSteps.find((s) => s.id === step.id);
        return existingStep ? { ...step, isOpen: existingStep.isOpen } : step;
      });
    },
    setCurrentJob: (
      state,
      { payload }: PayloadAction<Partial<IOrchestrationServer>>
    ) => {
      state.currentJob = payload;
    },
    updateCurrentJobSteps: (
      state,
      { payload }: PayloadAction<IStepInformation>
    ) => {
      if (state.currentJob?.jobId === payload?.jobId) {
        const updatedJob = updateSteps(state.currentJob, payload);
        // Only update the steps, not the entire job to avoid type conflicts
        if (updatedJob.steps) {
          state.currentJob.steps = updatedJob.steps;
        }
      }
    },
    switchOpenStatus: (
      state,
      { payload }: PayloadAction<ISwitchOpenStatus>
    ) => {
      if (
        payload.swtichInstance === true &&
        state.orchestrationInstancePanelStep
      ) {
        return {
          ...state,
          orchestrationInstancePanelStep: {
            ...state.orchestrationInstancePanelStep,
            isOpen: !state.orchestrationInstancePanelStep.isOpen,
          },
        };
      } else {
        const index = state.propertiesPanelSteps.findIndex(
          (p) => p.id === payload.id
        );
        const updatedSteps = [...state.propertiesPanelSteps];
        updatedSteps[index] = {
          ...updatedSteps[index],
          isOpen: !updatedSteps[index]?.isOpen,
        };
        return {
          ...state,
          propertiesPanelSteps: updatedSteps,
        };
      }
    },
    updateCurrentJobStatus: (state, { payload }: PayloadAction<IJobStatus>) => {
      if (payload.jobId === state.currentJob?.jobId) {
        return {
          ...state,
          currentJob: {
            ...state.currentJob,
            originalStatus: payload.status as ORCHESTRATION_STATUSES,
            status: combineStatus(payload.status as ORCHESTRATION_STATUSES),
            startTime: payload.startTime ?? state.currentJob?.startTime,
            endTime: payload.endTime ?? state.currentJob?.endTime,
          },
        };
      } else {
        return {
          ...state,
        };
      }
    },
  },
});

export const selectIndividualPanelStep = (state: RootState, id: number) =>
  state.propertiesPanel?.propertiesPanelSteps?.find((e) => e.id === id);

export const selectPropertiesPanel = (state: RootState) =>
  state.propertiesPanel?.propertiesPanelSteps;

export const selectOrchetsrationInstanceStep = (state: RootState) =>
  state.propertiesPanel?.orchestrationInstancePanelStep;

export const selectCurrentJob = (state: RootState) =>
  state.propertiesPanel?.currentJob;

export const {
  switchOpenStatus,
  setCurrentJob,
  updateCurrentJobSteps,
  setInitialPropertiesPanelValues,
  updateCurrentJobStatus,
} = propertiesPanelSlice.actions;

export default propertiesPanelSlice.reducer;
